import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Lock, FileText, MessageSquare, Clock, Bookmark, Settings, LogOut, LayoutDashboard, Shield, CreditCard, Coins, Building2, Bot } from "lucide-react";
import { APP_NAME } from "../config/branding";
import Footer from "./Footer";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/upload", label: "Upload", icon: FileText },
    { path: "/ask", label: "Ask", icon: MessageSquare },
    { path: "/agent/chat", label: "Contract Expert", icon: Bot, badge: "New" },
    { path: "/history", label: "History", icon: Clock },
    { path: "/saved", label: "Saved", icon: Bookmark },
  ];

  const isActive = (path) => {
    if (path === "/ask") return location.pathname.startsWith("/ask");
    if (path === "/agent/chat") return location.pathname.startsWith("/agent");
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-background noise-bg flex flex-col">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
              <span className="font-heading font-semibold text-lg">{APP_NAME}</span>
            </Link>

            {/* Nav */}
            {user && (
              <nav className="hidden md:flex items-center gap-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-3 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
                      isActive(item.path)
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                    {item.badge && (
                      <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-1 bg-primary/10 text-primary">
                        {item.badge}
                      </Badge>
                    )}
                  </Link>
                ))}
              </nav>
            )}

            {/* Right side */}
            <div className="flex items-center gap-3">
              {user ? (
                <>
                  {/* Credits Display */}
                  {location.pathname === '/billing' ? (
                    <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary/50">
                      <Coins className="w-4 h-4 text-teal-600" />
                      <span className="text-sm font-medium">{user?.credits_remaining || 0}</span>
                      <Badge variant="outline" className="text-xs">
                        {user?.plan_type?.toUpperCase() || 'FREE'}
                      </Badge>
                    </div>
                  ) : (
                    <Link 
                      to="/billing"
                      className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary/50 hover:bg-secondary transition-colors"
                    >
                      <Coins className="w-4 h-4 text-teal-600" />
                      <span className="text-sm font-medium">{user?.credits_remaining || 0}</span>
                      <Badge variant="outline" className="text-xs">
                        {user?.plan_type?.toUpperCase() || 'FREE'}
                      </Badge>
                    </Link>
                  )}

                  {/* Privacy indicator */}
                  <div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground bg-secondary/50 px-2 py-1 rounded-sm">
                    <Lock className="w-3 h-3" />
                    <span>Private Session</span>
                  </div>

                  {/* User menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="flex items-center gap-2 px-2 py-1 rounded-sm hover:bg-secondary transition-colors"
                        data-testid="user-menu-trigger"
                      >
                        <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                          {user?.name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="hidden sm:block text-sm">{user?.name}</span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem asChild>
                        <Link to="/billing" className="flex items-center gap-2" data-testid="billing-link">
                          <CreditCard className="w-4 h-4" />
                          Billing
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/settings" className="flex items-center gap-2" data-testid="settings-link">
                          <Settings className="w-4 h-4" />
                          Settings
                        </Link>
                      </DropdownMenuItem>
                      {(user?.organization_id || user?.org_name) && (
                        <DropdownMenuItem asChild>
                          <Link to="/org" className="flex items-center gap-2" data-testid="org-link">
                            <Building2 className="w-4 h-4" />
                            Organization
                          </Link>
                        </DropdownMenuItem>
                      )}
                      {user?.is_admin && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2" data-testid="admin-link">
                            <Shield className="w-4 h-4" />
                            Admin
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                /* Show Login/Signup buttons when not authenticated */
                <div className="flex items-center gap-2">
                  <Link to="/login">
                    <Button variant="ghost" size="sm">
                      Log In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button size="sm">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="md:hidden border-t border-border">
          <div className="flex overflow-x-auto px-4 py-2 gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm whitespace-nowrap rounded-sm ${
                  isActive(item.path)
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground"
                }`}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 relative z-10">
        {children}
      </main>

      {/* Disclaimer bar */}
      <div className="disclaimer-bar py-2 px-4 text-center text-muted-foreground">
        <p className="text-xs">Educational tool only. Not legal advice. Always consult HR, labor relations professionals, or legal counsel before taking action.</p>
      </div>

      {/* Footer with legal links */}
      <Footer />
    </div>
  );
};

export default Layout;
